import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Drawer,
  Descriptions,
  Space,
  Tooltip,
  Empty,
  Divider,
  message,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SafetyCertificateOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import {
  getErasureRequests,
  setErasureStatus,
  executeComplianceOnlyAnonymization,
  getErasurePreview,
  type ErasureRequest,
  type ErasurePreview,
  type PreviewLine,
} from '@/services/dataGovernanceService';
import {
  DryRunBanner,
  ComplianceStatusBadge,
  LegalHoldBadge,
  ConfirmDangerDialog,
  ComplianceTimeline,
  type TimelineEvent,
} from '@/components/compliance';

// ---------------------------------------------------------------------------
// Status filter options (all + each ErasureStatus).
// ---------------------------------------------------------------------------
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'REVIEWED', label: 'Reviewed' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'BLOCKED_BY_LEGAL_HOLD', label: 'Blocked by legal hold' },
];

const shortId = (id?: string | null) => (id ? id.slice(0, 8) : '—');

// The backend status literals expected by setErasureStatus (DSAR mapping).
type PendingKind = 'reject' | 'approve' | 'anonymize' | null;

// ---------------------------------------------------------------------------
// ErasurePreviewPanel — four honest, clearly separated preview sections.
// ---------------------------------------------------------------------------
const PREVIEW_LINE_COLUMNS: ColumnsType<PreviewLine> = [
  { title: 'Service', dataIndex: 'service_name', key: 'service_name', width: 180 },
  { title: 'Record category', dataIndex: 'record_category', key: 'record_category', width: 200 },
  {
    title: 'Count',
    dataIndex: 'count',
    key: 'count',
    width: 80,
    render: (n: number) => <Tag>{n}</Tag>,
  },
  { title: 'Reason', dataIndex: 'reason', key: 'reason' },
];

const PreviewSection: React.FC<{
  title: string;
  color: string;
  lines: PreviewLine[];
}> = ({ title, color, lines }) => (
  <div style={{ marginBottom: 20 }}>
    <div
      style={{
        fontWeight: 700,
        color,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          background: color,
        }}
      />
      {title} <span style={{ color: '#7A8099', fontWeight: 500 }}>({lines.length})</span>
    </div>
    {lines.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="None" />
    ) : (
      <Table<PreviewLine>
        size="small"
        rowKey={(r) => `${r.service_name}:${r.record_category}`}
        columns={PREVIEW_LINE_COLUMNS}
        dataSource={lines}
        pagination={false}
      />
    )}
  </div>
);

const ErasurePreviewPanel: React.FC<{ preview: ErasurePreview }> = ({ preview }) => (
  <Card
    size="small"
    style={{ marginTop: 12, background: '#FAFAFB' }}
    title={
      <Space>
        <ExperimentOutlined style={{ color: '#1677ff' }} />
        <span style={{ fontWeight: 700 }}>DRY-RUN preview — no records modified</span>
        <Tag color="blue">dry_run: true</Tag>
      </Space>
    }
  >
    <div style={{ fontSize: 12, color: '#7A8099', marginBottom: 12 }}>
      Generated at {fmtDateTime(preview.generated_at)}
      {preview.policy_id ? ` · policy ${preview.policy_id}` : ''}
    </div>
    <PreviewSection
      title="Will anonymize (compliance-service only)"
      color="var(--color-success)"
      lines={preview.will_anonymize}
    />
    <PreviewSection title="Will retain (by policy)" color="#1677ff" lines={preview.will_retain} />
    <PreviewSection title="Blocked by legal hold" color="var(--color-danger)" lines={preview.blocked} />
    <PreviewSection
      title="Not supported in this release (cross-service)"
      color="#8c8c8c"
      lines={preview.unsupported}
    />
  </Card>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const ErasureRequestsPage: React.FC = () => {
  const canView = useAppSelector(selectHasPermission('compliance:erasure_request:view'));
  const canReview = useAppSelector(selectHasPermission('compliance:erasure_request:review'));
  const canApprove = useAppSelector(selectHasPermission('compliance:erasure_request:approve'));
  const canExecute = useAppSelector(
    selectHasPermission('compliance:erasure_request:execute_compliance_only'),
  );

  const [rows, setRows] = useState<ErasureRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<ErasureRequest | null>(null);

  const [preview, setPreview] = useState<ErasurePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [pending, setPending] = useState<PendingKind>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getErasureRequests(statusFilter === 'ALL' ? undefined : statusFilter);
      setRows(data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load erasure requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, canView]);

  const openDetail = (req: ErasureRequest) => {
    setSelected(req);
    setPreview(null);
    setDrawerOpen(true);
  };

  const closeDetail = () => {
    setDrawerOpen(false);
    setSelected(null);
    setPreview(null);
    setPending(null);
  };

  const runPreview = async () => {
    if (!selected) return;
    setPreviewLoading(true);
    try {
      const p = await getErasurePreview(selected.id);
      setPreview(p);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to generate erasure preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const markReviewed = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await setErasureStatus(selected.id, 'in_review');
      message.success('Request marked as reviewed.');
      closeDetail();
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to mark request reviewed.');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmPendingAction = async (_reason: string) => {
    if (!selected || !pending) return;
    setActionLoading(true);
    try {
      if (pending === 'reject') {
        await setErasureStatus(selected.id, 'rejected');
        message.success('Request rejected.');
      } else if (pending === 'approve') {
        await setErasureStatus(selected.id, 'approved');
        message.success('Request approved.');
      } else if (pending === 'anonymize') {
        await executeComplianceOnlyAnonymization(selected.id);
        message.success('Compliance-service records anonymized.');
      }
      setPending(null);
      closeDetail();
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const columns: ColumnsType<ErasureRequest> = [
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      key: 'employee_id',
      render: (id: string) => <code>{shortId(id)}</code>,
    },
    { title: 'Type', dataIndex: 'request_type', key: 'request_type' },
    { title: 'Source', dataIndex: 'request_source', key: 'request_source' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <ComplianceStatusBadge kind="erasure" value={status} />,
    },
    {
      title: 'Legal hold',
      dataIndex: 'legal_hold_status',
      key: 'legal_hold_status',
      render: (v: string) => <LegalHoldBadge active={v === 'ACTIVE'} />,
    },
    {
      title: 'Requested',
      dataIndex: 'requested_at',
      key: 'requested_at',
      render: (ts: string) => (ts ? fmtDate(ts) : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, req: ErasureRequest) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(req)}>
          Open
        </Button>
      ),
    },
  ];

  const timelineEvents = (req: ErasureRequest): TimelineEvent[] => {
    const ev: TimelineEvent[] = [];
    if (req.requested_at)
      ev.push({ ts: req.requested_at, label: 'Requested', actor: req.requested_by, color: 'gold' });
    if (req.reviewed_at)
      ev.push({ ts: req.reviewed_at, label: 'Reviewed', actor: req.reviewed_by, color: 'blue' });
    if (req.approved_at)
      ev.push({ ts: req.approved_at, label: 'Approved', actor: req.approved_by, color: 'cyan' });
    if (req.completed_at)
      ev.push({ ts: req.completed_at, label: 'Completed', color: 'green' });
    return ev;
  };

  if (!canView) {
    return (
      <Card>
        <Empty description="You do not have permission to view erasure requests." />
      </Card>
    );
  }

  const legalHoldActive = selected?.legal_hold_status === 'ACTIVE';
  const canAnonymizeStatus =
    selected?.status === 'APPROVED' || selected?.status === 'REVIEWED';

  return (
    <div>
      <Card>
        <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyCertificateOutlined /> Erasure Requests
          </h2>
          <div style={{ color: '#7A8099' }}>
            Privacy erasure &amp; anonymization — dry-run first, compliance-service only.
          </div>
        </Space>

        <DryRunBanner />

        <Space style={{ marginBottom: 16 }}>
          <span style={{ color: '#7A8099' }}>Status</span>
          <Select
            style={{ width: 220 }}
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
        </Space>

        <Table<ErasureRequest>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{
            emptyText: <Empty description="No erasure requests found." />,
          }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </Card>

      <Drawer
        title={
          <Space>
            <span>Erasure Request</span>
            {selected && <ComplianceStatusBadge kind="erasure" value={selected.status} />}
          </Space>
        }
        width={720}
        open={drawerOpen}
        onClose={closeDetail}
        destroyOnClose
      >
        {selected && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Request ID">{selected.id}</Descriptions.Item>
              <Descriptions.Item label="Tenant ID">{selected.tenant_id}</Descriptions.Item>
              <Descriptions.Item label="Employee ID">{selected.employee_id}</Descriptions.Item>
              <Descriptions.Item label="Type">{selected.request_type}</Descriptions.Item>
              <Descriptions.Item label="Source">{selected.request_source}</Descriptions.Item>
              <Descriptions.Item label="Reason">{selected.reason || '—'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <ComplianceStatusBadge kind="erasure" value={selected.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Legal hold">
                <LegalHoldBadge active={legalHoldActive} />
              </Descriptions.Item>
              <Descriptions.Item label="Requested at">
                {selected.requested_at ? fmtDateTime(selected.requested_at) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed by">
                {selected.reviewed_by || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Approved by">
                {selected.approved_by || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Completed at">
                {selected.completed_at ? fmtDateTime(selected.completed_at) : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Workflow</Divider>
            <ComplianceTimeline events={timelineEvents(selected)} />

            <Divider orientation="left">Erasure preview</Divider>
            <Button
              icon={<ExperimentOutlined />}
              onClick={runPreview}
              loading={previewLoading}
            >
              Run Erasure Preview
            </Button>
            {preview && <ErasurePreviewPanel preview={preview} />}

            <Divider orientation="left">Actions</Divider>
            <Space wrap>
              {canReview && selected.status === 'REQUESTED' && (
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={actionLoading}
                  onClick={markReviewed}
                >
                  Mark Reviewed
                </Button>
              )}

              {(canReview || canApprove) && (
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => setPending('reject')}
                >
                  Reject Request
                </Button>
              )}

              {canApprove && (
                <Tooltip title={legalHoldActive ? 'Blocked by Legal Hold' : ''}>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    disabled={legalHoldActive}
                    onClick={() => setPending('approve')}
                  >
                    Approve Request
                  </Button>
                </Tooltip>
              )}

              {canExecute && canAnonymizeStatus && (
                <Tooltip title={legalHoldActive ? 'Blocked by Legal Hold' : ''}>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={legalHoldActive}
                    onClick={() => setPending('anonymize')}
                  >
                    Anonymize Eligible Compliance Records
                  </Button>
                </Tooltip>
              )}

              <Tooltip title="Evidence report export not available in this release.">
                <Button icon={<DownloadOutlined />} disabled>
                  Download Evidence Report
                </Button>
              </Tooltip>
            </Space>
          </>
        )}
      </Drawer>

      {/* Reject */}
      <ConfirmDangerDialog
        open={pending === 'reject'}
        title="Reject erasure request"
        description="The request will be marked as rejected. The subject may be notified. This does not modify any records."
        confirmLabel="Reject Request"
        loading={actionLoading}
        onConfirm={confirmPendingAction}
        onCancel={() => setPending(null)}
      />

      {/* Approve */}
      <ConfirmDangerDialog
        open={pending === 'approve'}
        title="Approve erasure request"
        description="Approving marks the request eligible for compliance-service anonymization. No records are modified by this step."
        confirmLabel="Approve Request"
        loading={actionLoading}
        onConfirm={confirmPendingAction}
        onCancel={() => setPending(null)}
      />

      {/* Anonymize (compliance-service only) */}
      <ConfirmDangerDialog
        open={pending === 'anonymize'}
        title="Anonymize eligible compliance records"
        warning="Only compliance-service records are affected."
        description="This anonymizes ONLY compliance-service-owned records for this subject and emits a cross-service erasure signal. Employee, payroll, leave, and document data are NOT modified."
        confirmLabel="Anonymize Compliance Records"
        loading={actionLoading}
        onConfirm={confirmPendingAction}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default ErasureRequestsPage;
